"use client";
import { WeeklyDealsHome } from "@/components/bnp-illustrations";
import { Hero, PropertyFilters, Pagination } from "@/components/bnp-ui";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";
import {
  BuyDetailCard,
  LandDetailCard,
  RentalDetailCard,
  ShortletDetailCard,
} from "@/components/shared";
import useAuthToken from "@/hooks/useAuthToken";
import { PropertyService } from "@/services";
import { useQuery } from "@tanstack/react-query";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import React, { useEffect, useRef, useState } from "react";
import GoogleAd from "@/components/shared/GoogleAd";
import FooterAd from "@/components/shared/ads/FooterAd";
import { usePageAds } from "@/hooks/usePageAds";
import { AdPage, AdPlacement } from "@/types/advertising";
import { AdRenderer } from "@/components/shared/ads/AdRenderer";
import { track } from "@/utils/track";
import parseHttpError from "@/utils/parseHttpError";
import ModernMinimalistFilters from "@/components/bnp-ui/property-filter/ModernPropertyFilters";
import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";
import { getAddress } from "@/utils/location";
import { Property } from "@/types/property";
import PropertyListingCard from "@/components/bnp-ui/listing-card/PropertyListingCard";
import PropertyListingSkeleton from "@/components/bnp-ui/listing-card/PropertyListingSkeleton";
import NoPropertyFound from "@/components/bnp-ui/helper_ui/NoPropertyFound";
import { ContentSidebar } from "@/components/layouts/Others/ContentSidebar";
import LocationRequest from "@/components/functional/LocationRequest";
import { useLocation } from "@/contexts/LocationContext";

const cleanQueryString = (
  val: string | string[] | undefined,
): string | undefined => {
  if (!val) return undefined;
  return Array.isArray(val) ? val[0] : val;
};

const cleanQueryBool = (val: any): boolean | undefined => {
  if (val === undefined || val === null || val === "") return undefined;
  return val === "true" || val === true;
};

const runtimeEnvironment = getRuntimeEnvironment();

const WeeklyDeals = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">(
    "default",
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [screenWidth, setScreenWidth] = useState<number | null>(null);
  const { userLocation } = useLocation();
  const pageSize = 12;
  const { user, token } = useAuthToken();
  const router = useRouter();
  // NOTE: The backend only serves ads on the homepage for now. We fetch the
  // homepage ad set here so the placements render while testing; swap back to
  // AdPage.WEEKLY_DEALS once real page ads are available.
  const { inFeedAds, footerAds, headerAds, popupAds, sidebarAds } = usePageAds(
    AdPage.HOME,
  );

  const handleCardClick = (property: Property) => {
    router.push(`/property/${property.id}`);
  };

  useEffect(() => {
    const updateWidth = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const {
    location,
    state,
    city,
    bedroomCount,
    bathroomCount,
    listingType,
    propertyType,
    propertyCategory,
    realtorId,
    maxPrice,
    minPrice,
    furnished,
    serviced,
    shared,
    addedToSite,
    searchQuery,
    page,
  } = router.query;

  const trackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTrackRef = useRef<{ page: number; filterCount: number } | null>(
    null,
  );

  useEffect(() => {
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);

    trackTimerRef.current = setTimeout(() => {
      const filterCount = [
        location,
        state,
        city,
        bedroomCount,
        bathroomCount,
        listingType,
        propertyType,
        propertyCategory,
        realtorId,
        maxPrice,
        minPrice,
        furnished,
        serviced,
        shared,
        addedToSite,
        searchQuery,
      ].filter(Boolean).length;

      const snapshot = { page: currentPage, filterCount };
      const prev = prevTrackRef.current;

      if (
        prev &&
        prev.page === snapshot.page &&
        prev.filterCount === snapshot.filterCount
      ) {
        return;
      }

      prevTrackRef.current = snapshot;

      track("user_checked_weekly_deals", {
        page: currentPage,
        ...(filterCount > 0 && { filter_count: filterCount }),
      });
    }, 300);

    return () => {
      if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    location,
    state,
    city,
    bedroomCount,
    bathroomCount,
    listingType,
    propertyType,
    propertyCategory,
    realtorId,
    maxPrice,
    minPrice,
    furnished,
    serviced,
    shared,
    addedToSite,
    searchQuery,
  ]);

  useEffect(() => {
    if (page) {
      setCurrentPage(Number(page));
    } else {
      setCurrentPage(1);
    }
  }, [page]);

  const getWeeklyDealsRequest = async () => {
    try {
      const queryParams = {
        ...(location && { location: cleanQueryString(location) }),
        ...(state && { state: cleanQueryString(state) }),
        ...(city && { city: cleanQueryString(city) }),
        ...(bedroomCount && { bedroomCount: cleanQueryString(bedroomCount) }),
        ...(bathroomCount && {
          bathroomCount: cleanQueryString(bathroomCount),
        }),
        ...(listingType && { listingType: cleanQueryString(listingType) }),
        ...(propertyType && { propertyType: cleanQueryString(propertyType) }),
        ...(propertyCategory && {
          propertyCategory: cleanQueryString(propertyCategory),
        }),
        ...(realtorId && { realtorId: cleanQueryString(realtorId) }),
        ...(maxPrice && { maxPrice: cleanQueryString(maxPrice) }),
        ...(minPrice && { minPrice: cleanQueryString(minPrice) }),
        ...(shared && { shared: cleanQueryBool(shared) }),
        ...(serviced && { serviced: cleanQueryBool(serviced) }),
        ...(addedToSite && { addedToSite: cleanQueryBool(addedToSite) }),
        ...(furnished !== undefined && {
          furnished: cleanQueryBool(furnished),
        }),
        ...(searchQuery && { searchQuery: cleanQueryString(searchQuery) }),
        page: currentPage,
        pageSize,
      };

      const response = await PropertyService.GetDeals(
        !queryParams.location && userLocation
          ? {
              ...queryParams,
              userCurrentLocation: await getAddress(
                userLocation.lat,
                userLocation.lng,
              ),
              latitude: userLocation.lat.toString(),
              longitude: userLocation.lng.toString(),
            }
          : queryParams,
      );
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: propertiesData,
    isLoading: propertiesIsLoading,
    isError: propertiesIsError,
    refetch: propertiesRefetch,
    isSuccess: propertiesIsSuccess,
  } = useQuery({
    queryKey: [
      "weekly-deals",
      location,
      state,
      city,
      bedroomCount,
      bathroomCount,
      listingType,
      propertyType,
      propertyCategory,
      realtorId,
      maxPrice,
      minPrice,
      furnished,
      currentPage,
      serviced,
      shared,
      addedToSite,
      searchQuery,
      pageSize,
      userLocation?.lat,
      userLocation?.lng,
    ],
    queryFn: getWeeklyDealsRequest,
    staleTime: 60 * 60 * 1000,
  });

  const handlePageChange = (pageNumber: number) => {
    const newQuery = {
      ...router.query,
      page: pageNumber.toString(),
    };

    router.push({
      pathname: router.pathname,
      query: newQuery,
    });
  };

  const sortProperties = (data: Property[]) => {
    if (sortOrder === "default") return data;
    return [...data].sort((a, b) => {
      const aValue = a.total_package || 0;
      const bValue = b.total_package || 0;
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });
  };

  useEffect(() => {
    if (propertiesData) {
      const sortedProperties = sortProperties(propertiesData?.data);
      setProperties(sortedProperties);
    }
  }, [propertiesData, sortOrder]);

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOrder(event.target.value as "default" | "asc" | "desc");
  };

  return (
    <HomeWrapper
      footerSlot={footerAds[0] ? <FooterAd ad={footerAds[0]} /> : undefined}
    >
      {headerAds[0] && (
        <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
          <AdRenderer ad={headerAds[0]} placement={AdPlacement.HEADER} />
        </div>
      )}

      <>
        <div className="w-full container mx-auto gap-4 mb-5 sticky top-[64px] lg:top-[94px] z-20 bg-white">
          <ModernMinimalistFilters />
        </div>
      </>

      <ContentSidebar
        sidebarContent={
          sidebarAds?.[0] && (
            <div className="w-full mx-auto px-4 pt-4 pb-2">
              <AdRenderer ad={sidebarAds[0]} placement={AdPlacement.SIDEBAR} />
            </div>
          )
        }
        topOffset={200}
      >
        <div className={containerWidth}>
          <div className="flex flex-col items-center w-full  mb-8 lg:mb-16 overflow-x-clip">
            <LocationRequest />
            {propertiesIsLoading && <PropertyListingSkeleton />}

            {propertiesIsError && <></>}

            {!propertiesIsError && !propertiesIsLoading && (
              <>
                {properties.length > 0 ? (
                  <>
                    <div className="flex flex-col lg:w-fit w-[100%]">
                      <div className="p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-5 w-full">
                        {properties.slice(0, 6).map((property: Property) => (
                          <PropertyListingCard
                            key={property.id}
                            property={property}
                            onCardClick={handleCardClick}
                          />
                        ))}
                      </div>
                    </div>
                    {/* In-feed ad placed between the first two rows and the
                          second two rows (after the 6th property) */}
                    {inFeedAds[0] && properties.length > 6 && (
                      <div className="col-span-1 md:col-span-2 lg:col-span-3 w-full my-4">
                        <AdRenderer
                          ad={inFeedAds[0]}
                          placement={AdPlacement.IN_FEED}
                        />
                      </div>
                    )}

                    <div className="flex flex-col lg:w-fit w-[100%]">
                      {" "}
                      <div className="p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-5 w-full">
                        {properties.slice(6).map((property: Property) => (
                          <PropertyListingCard
                            key={property.id}
                            property={property}
                            onCardClick={handleCardClick}
                          />
                        ))}
                      </div>
                    </div>
                    <Pagination
                      metadata={propertiesData?.metadata}
                      currentPage={currentPage}
                      onPageChange={handlePageChange}
                      className="w-full mt-8"
                      pageSize={pageSize}
                    />
                  </>
                ) : (
                  <NoPropertyFound />
                )}
              </>
            )}
          </div>
        </div>
      </ContentSidebar>

      <div className=" w-[100%] lg:w-[800px] flex justify-center my-8">
        <GoogleAd
          adSlot={process.env.NEXT_PUBLIC_AD_SLOT_BANNER || ""}
          adFormat="horizontal"
          fullWidthResponsive={true}
          className="w-full"
        />
      </div>

      {popupAds[0] && (
        <AdRenderer ad={popupAds[0]} placement={AdPlacement.POPUP} />
      )}
      {footerAds[0] && (
        <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
          <AdRenderer ad={footerAds[0]} placement={AdPlacement.FOOTER} />
        </div>
      )}
    </HomeWrapper>
  );
};

export default WeeklyDeals;
